import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { RefreshCw, X } from 'lucide-react';

/**
 * Detects when a new Service Worker is waiting and prompts the user to refresh.
 * Fixes the "stale SW serves 503 cached response" issue users hit after deployments.
 */
const SWUpdateBanner = () => {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let reg;
    let refreshing = false;

    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };

    const trackInstalling = (worker) => {
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          // A new SW is installed and waiting → show the banner
          setWaitingWorker(worker);
          setShow(true);
        }
      });
    };

    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration) return;
      reg = registration;

      // Case 1: a SW is already waiting (e.g. user came back after deploy)
      if (registration.waiting && navigator.serviceWorker.controller) {
        setWaitingWorker(registration.waiting);
        setShow(true);
      }

      // Case 2: a new SW starts installing during this session
      registration.addEventListener('updatefound', () => {
        if (registration.installing) trackInstalling(registration.installing);
      });

      // Proactively check for updates every 5 minutes (cheap HEAD request)
      const interval = setInterval(() => {
        registration.update().catch(() => {});
      }, 5 * 60 * 1000);

      // Cleanup interval on page unload
      window.addEventListener('beforeunload', () => clearInterval(interval), { once: true });
    });

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const handleRefresh = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback: force reload + clear caches
      if ('caches' in window) {
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
          .finally(() => window.location.reload());
      } else {
        window.location.reload();
      }
    }
  };

  const handleDismiss = () => setShow(false);

  if (!show) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] max-w-md w-[calc(100%-2rem)]"
      data-testid="sw-update-banner"
    >
      <div className="dossier-card !p-4 flex items-center gap-3 shadow-2xl border-l-4 border-l-[var(--gold,#B5894A)]">
        <RefreshCw className="w-5 h-5 text-[var(--gold,#B5894A)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight">Nouvelle version disponible</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Rechargez pour bénéficier des dernières corrections.
          </p>
        </div>
        <Button
          size="sm"
          onClick={handleRefresh}
          className="flex-shrink-0"
          data-testid="sw-update-refresh-btn"
        >
          Recharger
        </Button>
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          data-testid="sw-update-dismiss-btn"
          aria-label="Plus tard"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SWUpdateBanner;
